import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { datePartDateTrunc, datetimePartDateTrunc } from 'src/app/utils/sql';
import { IDatasourceColumnSQLMap } from '../../../utils/datasource-sql-map/datasource';

export enum QueryContextSettingID {
    ABTESTS_MAX_DAY_NO = 'abtests.maxDayNo',
    ABTESTS_UPLIFT_REFERENCE = 'abtests.upliftReference',
    CAMPAIGN_ACTIVE_ALL = 'campaign.activeAll',
    CAMPAIGNS_SELECTED_METRICS = 'campaigns.selectedMetrics',
    CUSTOM_FUNNELS_OPEN_CLOSED = 'customfunnels.openClosed',
    DATE_AGGREGATION = 'dateAggregation',
    DATETIME_AGGREGATION = 'datetimeAggregation',
    FUNNELS_LIMIT_DETAIL_STEPS = 'funnels.limitDetailSteps',
    FUNNELS_STEP_COUNT_STRATEGY = 'funnels.stepCountStrategy',
    FUNNELS_OTHER_GROUPING_STRATEGY = 'funnels.otherGroupingStrategy',
    NET_GROSS = 'netGross',
    CURRENCY = 'currency',
    REALTIME_CLOUDSAVE_NORMALIZE_METRICS = 'cloudsave.normalizeMetrics',
    REALTIME_HOURS_UP_TO_LATEST = 'realtimeHoursUpToLatest',
    REALTIME_NORMALIZE_METRICS = 'realtimeNormaliseMetrics',
    REALTIME_CURRENT_WEEKDAY = 'realtimeCurrentWeekday',
    REVENUE_TYPE = 'revenueType',
    ROAS_SELECTED_DAY_NO = 'roas.selectedDayNo',
    SHOW_TOTAL_REVENUE = 'showTotalRevenue',
    SHOW_INAPP_REVENUE = 'showInappRevenue',
    SHOW_AD_REVENUE = 'showAdRevenue',
    SKADNETWORK_SELECTED_METRICS = 'skadnetwork.selectedMetrics',
    RETENTION_SELECTED_DAY_NO = 'retention.selectedDayNo',
    RETENTION_MAX_DAY_NO = 'retention.maxDayNo',
    ENGAGEMENT_MAX_DAY_NO = 'engagement.maxDayNo',
    ENGAGEMENT_SELECTED_DAY_AND_SESSION = 'engagement.selectedDayAndSession',
}

export interface IQueryContextSetting {
    id: QueryContextSettingID;
    value: any;
}

export interface IQueryContextDatasourceColumnState {
    value: any;
    isFilterApplied: boolean;
    isSplitApplied: boolean;
}

export interface IQueryContextDatasourceColumn {
    id: IDatasourceColumnSQLMap['columnId'];

    /**
     * State of a controller.
     * if defined statically in dashboard it can be initial state
     * TODO in future there should be an async function to get those state from i.e. local cache, firestore
     */
    state: IQueryContextDatasourceColumnState;
}

/**
 * An interface representing atomic (single) change in query context.
 */
export interface IQueryContextChange {
    setting?: IQueryContextSetting;
    datasourceColumn?: IQueryContextDatasourceColumn;
}

/**
 * An interface representing query context.
 * Contains all selected settings and used datasource columns. Related to query.
 * TODO: Refactor structure so that datasourceColumns are divided into filters and splits.
 */
export interface IQueryContext {
    settings: {
        [QueryContextSettingID.ABTESTS_MAX_DAY_NO]?: string;
        [QueryContextSettingID.ABTESTS_UPLIFT_REFERENCE]?: string;
        [QueryContextSettingID.CAMPAIGN_ACTIVE_ALL]?: 'active' | 'all';
        [QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS]?: string[];
        [QueryContextSettingID.CURRENCY]?: 'usd' | 'pln';
        [QueryContextSettingID.CUSTOM_FUNNELS_OPEN_CLOSED]?: 'open' | 'closed';
        [QueryContextSettingID.NET_GROSS]?: 'net' | 'gross';
        [QueryContextSettingID.DATE_AGGREGATION]?: datePartDateTrunc;
        [QueryContextSettingID.DATETIME_AGGREGATION]?: datetimePartDateTrunc;
        [QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO]?: number;
        [QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION]?: number[];
        [QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS]?: string;
        [QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY]?:
            | 'master_step_cnt'
            | 'total_cnt';
        [QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY]?:
            | 'raw'
            | 'dynamic'
            | 'master_step'
            | 'adventure';
        [QueryContextSettingID.REALTIME_CLOUDSAVE_NORMALIZE_METRICS]?:
            | 'none'
            | 'dau'
            | 'non_anonymous';
        [QueryContextSettingID.REALTIME_HOURS_UP_TO_LATEST]?: boolean;
        [QueryContextSettingID.REALTIME_NORMALIZE_METRICS]?: boolean;
        [QueryContextSettingID.REALTIME_CURRENT_WEEKDAY]?:
            | boolean
            | 'Monday'
            | 'Tuesday'
            | 'Wednesday'
            | 'Thursday'
            | 'Friday'
            | 'Saturday'
            | 'Sunday';
        [QueryContextSettingID.RETENTION_SELECTED_DAY_NO]?: (
            | '1'
            | '3'
            | '7'
            | '14'
            | '30'
            | '90'
            | '180'
            | '360'
        )[];
        [QueryContextSettingID.RETENTION_MAX_DAY_NO]?:
            | '60'
            | '90'
            | '180'
            | '360'
            | '720'
            | 'unlimited';
        [QueryContextSettingID.REVENUE_TYPE]?: 'total' | 'inapp' | 'ad';
        [QueryContextSettingID.ROAS_SELECTED_DAY_NO]?: (
            | '1'
            | '3'
            | '7'
            | '14'
            | '30'
            | '90'
            | '180'
            | '360'
        )[];
        [QueryContextSettingID.SHOW_TOTAL_REVENUE]?: boolean;
        [QueryContextSettingID.SHOW_INAPP_REVENUE]?: boolean;
        [QueryContextSettingID.SHOW_AD_REVENUE]?: boolean;
        [QueryContextSettingID.SKADNETWORK_SELECTED_METRICS]?: string[];
        // [key: string]: any;
    };
    datasourceColumns: IQueryContextDatasourceColumn[];
}

// Define default settings for query context
// export const QueryContextDefaultSettings: Partial<IQueryContext['settings']> = {
export const QueryContextDefaultSettings = {
    [QueryContextSettingID.ABTESTS_MAX_DAY_NO]: '30',
    // [QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS]:
    //     CampaignTableDashoardItemHelper.getAllDefaultMetrics()
    //         .map((metric: TableColumnItem) => metric.id)
    //         .filter((metricId: string) => {
    //             // exclude all that starts with 'arpu' or 'cpt' or 'roas_w' or 'roas_m' or 'retention_d0'
    //             return (
    //                 !metricId.startsWith('arpu') &&
    //                 !metricId.startsWith('cpt') &&
    //                 !metricId.startsWith('roas_w') &&
    //                 !metricId.startsWith('roas_m') &&
    //                 metricId !== 'retention_d0'
    //             );
    //         }),
    [QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO]: '30',
    [QueryContextSettingID.ENGAGEMENT_SELECTED_DAY_AND_SESSION]: [0],
    [QueryContextSettingID.FUNNELS_LIMIT_DETAIL_STEPS]: '500',
    [QueryContextSettingID.FUNNELS_OTHER_GROUPING_STRATEGY]: 'raw' as const,
    [QueryContextSettingID.FUNNELS_STEP_COUNT_STRATEGY]:
        'master_step_cnt' as const,
    [QueryContextSettingID.RETENTION_MAX_DAY_NO]: '180',
    [QueryContextSettingID.RETENTION_SELECTED_DAY_NO]: ['1', '7', '30'],
    // [QueryContextSettingID.SKADNETWORK_SELECTED_METRICS]:
    //     SkanTableDashoardItemHelper.tableSkanPostbacksColumnsGetter().map(
    //         (metric: TableColumnItem) => metric.id
    //     ),
};

/**
 *  Checks if any split is applied in query context
 * @param context
 * @returns boolean
 *
 */
export function isSplitApplied(context: IQueryContext): boolean {
    return context.datasourceColumns.some(
        (column) => column.state.isSplitApplied
    );
}
